import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import GuidedEvidenceForm from '../../components/ticket/GuidedEvidenceForm';
import { Ticket, TicketFields } from '../../types';
import { toast } from 'react-toastify';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { sessionService } from '../../services/session.service';
import { useTicket } from '../../contexts/TicketContext';
import { useAuth } from '../../contexts/AuthContext';
import { runGates } from '../../gating/gateEngine';
import { handleGateDecision } from '../../gating/gateHandler';
import { buildGateContext } from '../../gating/buildGateContext';
import { AuthGate, SubscriptionGate } from '../../gating/gates';
import { useTranslation } from 'react-i18next';
import { debugError, debugInfo } from '../../utils/debug';

const TicketEvidencePage: React.FC = () => {
  const { id: ticketId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const fieldsState = (location.state as any)?.fields as TicketFields | undefined;
  const ticketState = (location.state as any)?.ticket as Ticket | undefined;
  const { ticketFields, setSessionFromTicketResponse } = useTicket();
  const { user } = useAuth();
  const { t } = useTranslation(['tickets']);
  const hydratedFromStateRef = useRef(false);
  const [loading, setLoading] = useState(!fieldsState && !ticketFields);
  const [gateReady, setGateReady] = useState(false);
  const [gateChecking, setGateChecking] = useState(true);

  useEffect(() => {
    debugInfo('TicketEvidencePage mount', { ticketId, hasState: !!fieldsState, hasTicket: !!ticketState });

    if (!ticketId) return;
    if (fieldsState && !hydratedFromStateRef.current) {
      hydratedFromStateRef.current = true;
      setSessionFromTicketResponse({
        ticket: ticketState,
        fields: fieldsState,
        ticketId,
      });
      setLoading(false);
      return;
    }
    if (ticketFields) {
      setLoading(false);
      return;
    }
    const fetchData = async () => {
      try {
        const resp = await sessionService.getTicket(ticketId);
        if (resp.success && resp.data) {
          setSessionFromTicketResponse({
            ticket: resp.data.ticket,
            fields: resp.data.fields as TicketFields,
            ticketId,
          });
          debugInfo('Loaded ticket for evidence', { ticketId, fields: resp.data.fields });
        } else {
          throw new Error(resp.message || 'Failed to load ticket');
        }
      } catch (error: any) {
        toast.error(error.message || 'Failed to load ticket');
        debugError('Evidence page fetch error', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [ticketId, fieldsState, ticketState, ticketFields, setSessionFromTicketResponse]);

  useEffect(() => {
    if (!ticketId) return;
    let active = true;
    const search = new URLSearchParams(location.search);
    const stripeStatus = search.get('stripe');
    if (stripeStatus === 'success') {
      toast.success('Subscription active. You can upload evidence now.');
    }
    if (stripeStatus === 'cancel') {
      toast.info('Checkout canceled.');
    }

    const checkGate = async () => {
      setGateChecking(true);
      const returnUrl = `${location.pathname}${location.search || ''}`;
      const ctx = buildGateContext({
        user,
        returnUrl,
        refreshBilling: () => sessionService.getBillingMe(true),
      });
      const decision = await runGates([AuthGate, SubscriptionGate], ctx);
      const allowed = handleGateDecision(decision, {
        navigate,
        toast: (message) => toast.error(message),
        returnUrl,
      });
      if (active) {
        setGateReady(allowed);
        setGateChecking(false);
      }
    };
    checkGate();
    return () => {
      active = false;
    };
  }, [ticketId, location.search, navigate, user]);

  const fields = ticketFields || fieldsState;

  if (loading) {
    return (
        <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" text={t('evidence.loadingTicket', { ns: 'tickets' })} />
      </div>
    );
  }

  if (!ticketId || !fields) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-lg text-gray-700">{t('evidence.missingTicket', { ns: 'tickets' })}</p>
          <button className="btn-primary mt-4" onClick={() => navigate('/upload')}>
            {t('evidence.backToTickets', { ns: 'tickets' })}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {gateChecking ? (
        <div className="py-6 text-sm text-slate-500 text-center">
          {t('evidence.gateLoading', { ns: 'tickets' })}
        </div>
      ) : (
        gateReady && (
          <GuidedEvidenceForm
            ticketId={ticketId}
            extractedFields={fields}
            onComplete={() => {
              toast.success(t('evidence.submittedToast', { ns: 'tickets' }));
              navigate(`/ticket/${ticketId}/appeal`);
            }}
          />
        )
      )}
    </div>
  );
};

export default TicketEvidencePage;
